import { pricingSuffix, type CohortUser, type PricingSuffix } from "~/utils/pricing"

// Shape of a pricing card in the hardcoded plan lists below. Named so every generation declares
// its empty `yearly` array identically instead of repeating the literal.
export type PricingPackage = {
  title: string
  tag: string
  pkgRentals: number
  pkgDesc: string
  monthlyPrice: number
  yearlyPrice?: number
  period: string
  description: string
  actionLabel: string
  popular?: boolean
  features: string[]
}

export const packagesV2 = {
  monthly: [
    {
      title: "Quickie",
      tag: "quickie-v2",
      pkgRentals: 5,
      pkgDesc: "includes 5 video rentals",
      monthlyPrice: 20,
      period: "month",
      description: "Want a few videos a month? This is the perfect plan to explore at your own pace.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "5 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Daymaker",
      tag: "daymaker-v2",
      pkgRentals: 30,
      pkgDesc: "includes 30 video rentals",
      monthlyPrice: 60,
      period: "month",
      description: "Already sold? Start making MLNP a part of your daily life with a new video rental every day.",
      actionLabel: "Subscribe Now",
      popular: true,
      features: [
        "Access to thousands of intro videos",
        "30 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Binge",
      tag: "binge-v2",
      pkgRentals: 60,
      pkgDesc: "includes 60 video rentals",
      monthlyPrice: 100,
      period: "month",
      description: "Get come, get cozy and binge on #RealWorldSex! Watch a whole bunch of videos anytime you want.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "60 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Dedicated daily video recommendation email love letter from Cindy",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
  ],
  yearly: [] as PricingPackage[],
}

// v3 is v2 with exactly one price change: Quickie $20 -> $15. Every other field — copy, credit
// counts, feature lists, Daymaker $60, Binge $100 — is identical to packagesV2 by design. None of
// the v2 copy strings embed a price, which is what makes this a pure tag + price diff.
export const packagesV3 = {
  monthly: [
    {
      title: "Quickie",
      tag: "quickie-v3",
      pkgRentals: 5,
      pkgDesc: "includes 5 video rentals",
      monthlyPrice: 15,
      period: "month",
      description: "Want a few videos a month? This is the perfect plan to explore at your own pace.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "5 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Daymaker",
      tag: "daymaker-v3",
      pkgRentals: 30,
      pkgDesc: "includes 30 video rentals",
      monthlyPrice: 60,
      period: "month",
      description: "Already sold? Start making MLNP a part of your daily life with a new video rental every day.",
      actionLabel: "Subscribe Now",
      popular: true,
      features: [
        "Access to thousands of intro videos",
        "30 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Binge",
      tag: "binge-v3",
      pkgRentals: 60,
      pkgDesc: "includes 60 video rentals",
      monthlyPrice: 100,
      period: "month",
      description: "Get come, get cozy and binge on #RealWorldSex! Watch a whole bunch of videos anytime you want.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "60 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Dedicated daily video recommendation email love letter from Cindy",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
  ],
  yearly: [] as PricingPackage[],
}

// ---------------------------------------------------------------------------------------------
// v4 pricing generation.
//
// v4 is v2's prices, credits and recurrence EXACTLY, under new display names, with its own
// subscribe page (app/components/subscribe-v4.tsx). The experiment is v2 vs v4 and isolates the
// page; a price change here would confound it. (Draft copy quoted Taster at $15 — that is v3's
// Quickie price, deliberately NOT used.)
//
// Tag BASES are unchanged (quickie/daymaker/binge): billing, the annual-upgrade check
// (`${tag}-annual`), the dashboard and basePlanTag() all match on them. Only Name changes.
//
// TO CHANGE A v4 PRICE: edit monthlyPrice / annualPrice below — packagesV4, the v4 subscribe
// page, the promotion banner and lowestMonthlyPrice all read it. The CHARGE comes from
// Packages.Price in the database, so the matching row in prisma/seed.sql and
// prisma/manual/pricing-v4-launch.sql must change too; packagesV4.test.ts fails if the seed, the
// launch script and this table disagree.
//
// Annual: annualPrice is the yearly total of the -v4-annual row (10% off twelve months). Each v4
// card sells both: monthly at monthlyPrice, annual at annualPrice billed once a year. The rules
// for annual plans (recharge = one month top-off, cancel keeps the paid year) are in
// app/purchases/annual.ts. packagesV4.yearly stays empty: it feeds the legacy-style yearly toggle,
// which the v4 page doesn't have — its cards read annualPrice directly.
//
// description / annualDescription are the purchase dialog's copy (Packages.Description), so they
// live in the seed and launch SQL too; packagesV4.test.ts pins those to these strings.
// ---------------------------------------------------------------------------------------------
export type V4Plan = {
  base: "quickie" | "daymaker" | "binge"
  title: string
  tag: string
  monthlyPrice: number
  credits: number
  annualTitle: string
  annualTag: string
  // Yearly total charged by the -v4-annual row.
  annualPrice: number
  // Packages.Description of the monthly and annual rows: what the purchase dialog says.
  description: string
  annualDescription: string
  // The ✓ bullets on the v4 subscribe page, in display order. Each tier carries every perk of the
  // tier below it plus more, so a bigger plan always lists more bullets.
  perks: string[]
}

const V4_BASE_PERKS = [
  "Unlock over 5,000 free intro videos & stills",
  "Rent full videos with credits which never expire with an active subscription",
  "Receive personalized recommendations from the Curation Team concierge, upon request",
  "Chat with MakeLoveNotPornstars and members in comments sections and forum posts",
]
const V4_MEET_AND_GREET = "Meet MakeLoveNotPornstars on bi-annual meet & greet video calls"
// v2 Binge's existing perk, word for word — already delivered, so Banquet's extra bullet is not a
// new commitment.
const V4_CINDY_LOVE_LETTER = "Dedicated daily video recommendation email love letter from Cindy"

// "FIVE" for 5 credits. Spelled out from the credit count, never typed per plan, so the headline
// can't drift from what the plan awards. Counts without a word fall back to digits.
const NUMBER_WORDS: Record<number, string> = { 5: "Five", 10: "Ten", 30: "Thirty", 50: "Fifty", 60: "Sixty" }
export const videoCountWord = (credits: number) => NUMBER_WORDS[credits] ?? String(credits)

function v4Plan(
  base: V4Plan["base"],
  title: string,
  monthlyPrice: number,
  credits: number,
  annualPrice: number,
  perks: string[]
): V4Plan {
  const videos = `${videoCountWord(credits)} real world sex videos every month`
  return {
    base,
    title,
    tag: `${base}-v4`,
    monthlyPrice,
    credits,
    // Same rule v2 annual names follow: "Annual " + the monthly plan's Name.
    annualTitle: `Annual ${title}`,
    annualTag: `${base}-v4-annual`,
    annualPrice,
    description: `${videos}, with credits that never expire while you're subscribed.`,
    annualDescription: `${videos}, billed once a year, with credits that never expire while you're subscribed.`,
    perks,
  }
}

export const V4_PLANS: V4Plan[] = [
  v4Plan("quickie", "Taster", 20, 5, 216, V4_BASE_PERKS),
  v4Plan("daymaker", "Buffet", 60, 30, 648, [...V4_BASE_PERKS, V4_MEET_AND_GREET]),
  v4Plan("binge", "Banquet", 100, 60, 1080, [...V4_BASE_PERKS, V4_MEET_AND_GREET, V4_CINDY_LOVE_LETTER]),
]

// What the annual plan works out to, derived from the two prices so the page can never quote a
// saving the prices don't produce.
export function v4AnnualOffer(plan: V4Plan) {
  const perMonth = plan.annualPrice / 12
  return {
    perMonth,
    yearly: plan.annualPrice,
    savePercent: Math.round((1 - perMonth / plan.monthlyPrice) * 100),
  }
}

export const packagesV4 = {
  monthly: V4_PLANS.map(
    (plan): PricingPackage => ({
      title: plan.title,
      tag: plan.tag,
      pkgRentals: plan.credits,
      pkgDesc: `${videoCountWord(plan.credits)} videos / month`,
      monthlyPrice: plan.monthlyPrice,
      period: "month",
      description: plan.description,
      actionLabel: "Subscribe Now",
      features: plan.perks,
    })
  ),
  // The v4 page has no yearly toggle; its cards sell annual from V4_PLANS (see the header comment).
  yearly: [] as PricingPackage[],
}

export const packages = {
  monthly: [
    {
      title: "Quickie",
      tag: "quickie",
      pkgRentals: 5,
      pkgDesc: "includes 5 video rentals per month",
      monthlyPrice: 10,
      period: "month",
      description:
        "Want a few videos a month for a better price? This is the perfect plan to explore at your own pace. There’s a lot of #RealWorldSex to enjoy!",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 5 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Daymaker",
      tag: "daymaker",
      pkgRentals: 30,
      pkgDesc: "includes 30 video rentals per month",
      monthlyPrice: 30,
      period: "month",
      description:
        "Already sold? Start making MLNP a part of your daily life with a new video rental every day. Isn’t this more fun than Netflix?",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 30 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Binge",
      tag: "binge",
      pkgRentals: 50,
      pkgDesc: "includes 50 video rentals per month",
      monthlyPrice: 50,
      period: "month",
      description: "Get come, get cozy and binge on #RealWorldSex! Watch a whole bunch of videos anytime you want.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 50 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Dedicated daily video recommendation email love letter from Cindy",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
  ],
  yearly: [
    {
      title: "Annual Quickie",
      tag: "quickie-annual",
      pkgDesc: "includes 5 videos per month",
      monthlyPrice: 10,
      yearlyPrice: 100,
      period: "year",
      description:
        "Want a few videos a month for a better price? This is the perfect plan to explore at your own pace. There’s a lot of #RealWorldSex to enjoy!",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 5 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Annual Daymaker",
      tag: "daymaker-annual",
      pkgDesc: "includes 30 videos per month",
      monthlyPrice: 30,
      yearlyPrice: 300,
      period: "year",
      description:
        "Already sold? Start making MLNP a part of your daily life with a new video rental every day. Isn’t this more fun than Netflix?",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 30 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Annual Binge",
      tag: "binge-annual",
      pkgDesc: "includes 50 videos per month",
      monthlyPrice: 50,
      yearlyPrice: 500,
      period: "year",
      description: "Get come, get cozy and binge on #RealWorldSex! Watch a whole bunch of videos anytime you want.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 50 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Dedicated daily video recommendation email love letter from Cindy",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
  ],
}

// The plan list a viewer's pricing suffix selects. Declared after `packages` because these are
// `const` bindings — referencing `packages` above its declaration would throw at module load.
//
// Keyed by PricingSuffix so adding a generation is one line here plus a new packagesVN above, and
// so no call site can reintroduce the `isV2 ? packagesV2 : packages` bug that silently served a
// v3 viewer the LEGACY prices. Index it with pricingSuffix(user), never with a boolean.
export const packagesBySuffix: Record<PricingSuffix, { monthly: PricingPackage[]; yearly: PricingPackage[] }> = {
  "": packages,
  "-v2": packagesV2,
  "-v3": packagesV3,
  "-v4": packagesV4,
}

// The "from $X/month" a viewer is quoted: the cheapest monthly plan in their own generation.
export const lowestMonthlyPrice = (user: CohortUser | unknown) =>
  Math.min(...packagesBySuffix[pricingSuffix(user)].monthly.map((pkg) => pkg.monthlyPrice))
